/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Latitude / longitude as the organizer types them.
 *
 * Every point in the wizard lands in a `geography(Point,4326)` column, which
 * refuses anything that is not a pair of numbers. Until now the form let "abc"
 * sit in a coordinate box right up to publish, where the save failed with no
 * hint of which box was wrong. These rules catch it at the keystroke and again
 * before the save, and both places read them from here so they cannot drift.
 */

const BOUNDS = {
  lat: { label: 'Latitude', limit: 90 },
  lng: { label: 'Longitude', limit: 180 },
};

const isBlank = (value) => value === undefined || value === null || value === '';

/**
 * Strips what can never be part of a coordinate: everything but digits, one
 * decimal point and a leading minus.
 *
 * Partial numbers ("-", "12.") survive on purpose — they are how a number looks
 * halfway through being typed, and rejecting them would make "-12.5" untypeable.
 */
export function sanitizeCoordinate(raw) {
  const text = String(raw ?? '');
  const negative = text.trimStart().startsWith('-');
  const [whole, ...fraction] = text.replace(/[^\d.]/g, '').split('.');
  const digits = fraction.length ? `${whole}.${fraction.join('')}` : whole;
  return (negative ? '-' : '') + digits;
}

/**
 * The message for one coordinate, or null when it is fine.
 *
 * Blank is not an error here — whether a point is required is the caller's
 * call, and an empty box mid-form should not shout at anyone.
 */
export function coordinateError(axis, value) {
  if (isBlank(value)) return null;
  const { label, limit } = BOUNDS[axis];
  const number = Number(value);
  if (String(value).trim() === '' || !Number.isFinite(number)) {
    return `${label} must be a number.`;
  }
  if (number < -limit || number > limit) {
    return `${label} must be between -${limit} and ${limit}.`;
  }
  return null;
}

/** Both coordinates present. Says nothing about whether they are usable. */
export function hasPoint(point) {
  return !!point && !isBlank(point.lat) && !isBlank(point.lng);
}

/** Both coordinates present, numeric and on the planet. */
export function isValidPoint(point) {
  return hasPoint(point)
    && !coordinateError('lat', point.lat)
    && !coordinateError('lng', point.lng);
}
